import { observable, computed, makeAutoObservable } from 'mobx';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';
import { reduxImmutableHash } from './utils/reduxImmutableHash';
import { ReduxRawState } from './utils/ReduxRawState';

const UNIVERSES = {
    mcbookie: { label: 'MCB' },
    star: { label: 'STAR' },
    spin: { label: 'SPIN' },
};

const isUser = createGuard(
    t.interface({
        authenticated: t.boolean,
        data: t.record(t.string, t.unknown),
    })
);

const BackofficeUserDataTypeIO = t.interface({
    roles: t.array(t.string),
    universe: t.string,
    tableauAccessLogin: t.string,
});

const isUserData = createGuard(BackofficeUserDataTypeIO);

type BackofficeUserDataType = t.TypeOf<typeof BackofficeUserDataTypeIO>;

export class ReduxBackofficeUserState {
    @observable private userData: BackofficeUserDataType | null;

    public constructor(reduxStore: ReduxRawState) {
        this.userData = null;

        reduxImmutableHash(
            reduxStore,
            (state: Record<string, unknown>): unknown => state.user,
            (data: unknown): void => {
                if (data === undefined) {
                    return;
                }

                if (isUser(data)) {
                    if (isUserData(data.data)) {
                        this.userData = data.data;
                    }
                } else {
                    console.error('BackofficeUserState -> incorrect data', data);
                }
            }
        );
        makeAutoObservable(this);
    }

    @computed public get tableauAccessLogin(): string {
        if (this.userData === null) {
            return '';
        }
        return this.userData.tableauAccessLogin;
    }

    @computed public get roles(): Array<string> {
        if (this.userData === null) {
            return [];
        }
        return this.userData.roles;
    }

    @computed public get isSuperUser(): boolean {
        return this.roles.includes('superuser');
    }

    @computed public get isSuperUserOrSeniorTrader(): boolean {
        return this.roles.includes('superuser') || this.roles.includes('seniortrader');
    }

    @computed public get universePrefix(): string {
        if (this.userData === null) {
            return '';
        }
        const universe = this.userData.universe;
        if (universe === 'star') {
            return UNIVERSES.star.label;
        }
        if (universe === 'spin') {
            return UNIVERSES.spin.label;
        }
        if (universe === 'mcbookie') {
            return UNIVERSES.mcbookie.label;
        }

        return universe;
    }
}

// --> roles:
// 0: "superuser"
// 1: "all-staff"
// 2: "staff"
// 3: "trader"
// 4: "seniortrader"
// 5: "juniortrader"
// 6: "backofficestaff"
